import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  Brain, Users, BarChart3, AlertTriangle, RefreshCw,
  Sparkles, TrendingUp, GraduationCap, Loader2, Bell,
  Lightbulb, ClipboardList, PieChart
} from 'lucide-react';

// Componentes V2
import PainelGlobal from './PainelGlobal';
import ListaAlunos from './ListaAlunos';
import { DashboardTurma, DashboardExecutivo, AlertasInteligentes } from './Dashboards';

export default function DNACoreV2() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('painel');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [painelData, setPainelData] = useState(null);
  const [turmas, setTurmas] = useState([]);
  const [selectedTurmaId, setSelectedTurmaId] = useState('');
  
  useEffect(() => {
    loadInitialData();
  }, []);
  
  const loadInitialData = async () => {
    setLoading(true);
    try {
      const [painelRes, turmasRes] = await Promise.all([
        api.get('/dnacore/v2/painel-global'),
        api.get('/dnacore/v2/turmas')
      ]);
      
      setPainelData(painelRes.data);
      setTurmas(turmasRes.data.turmas || []);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados do painel');
    } finally {
      setLoading(false);
    }
  };
  
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const res = await api.get('/dnacore/v2/painel-global?force_refresh=true');
      setPainelData(res.data);
      toast.success('Dados atualizados com sucesso!');
    } catch (error) {
      toast.error('Erro ao atualizar dados');
    } finally {
      setRefreshing(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center" data-testid="dnacore-v2-loading">
        <div className="text-center">
          <div className="relative">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-purple-500 to-green-500 animate-pulse mx-auto" />
            <Brain className="w-10 h-10 text-white absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
          </div>
          <p className="text-white/70 mt-4 text-lg">Carregando DNACore...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900" data-testid="dnacore-v2-page">
      {/* Header Premium */}
      <div className="sticky top-0 z-40 backdrop-blur-xl bg-slate-900/80 border-b border-purple-500/20">
        <div className="max-w-[1600px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="relative">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-600 to-green-500 flex items-center justify-center shadow-lg shadow-purple-500/30">
                  <Brain className="w-7 h-7 text-white" />
                </div>
                <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-slate-900 animate-pulse" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                  DNACore
                  <span className="text-xs px-2 py-0.5 rounded-full bg-gradient-to-r from-purple-500 to-green-500 text-white font-medium">
                    V2
                  </span>
                </h1>
                <p className="text-purple-300/70 text-sm">Núcleo Pedagógico Inteligente</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {painelData?.from_cache && (
                <span className="text-xs text-purple-300/50 px-3 py-1 rounded-full bg-purple-500/10 border border-purple-500/20">
                  Cache • {new Date(painelData.updated_at).toLocaleTimeString('pt-BR')}
                </span>
              )}
              
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                disabled={refreshing}
                className="border-purple-500/30 text-purple-300 hover:bg-purple-500/20 hover:text-white"
                data-testid="refresh-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Atualizar
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/v2/dashboard')}
                className="border-purple-500/30 text-purple-300 hover:bg-purple-500/20"
              >
                Versão Clássica
              </Button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="max-w-[1600px] mx-auto px-6 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="w-full max-w-4xl mx-auto mb-8 bg-slate-800/50 border border-purple-500/20 p-1 rounded-xl flex-wrap h-auto gap-1">
            <TabsTrigger 
              value="painel" 
              className="flex-1 min-w-[120px] data-[state=active]:bg-gradient-to-r data-[state=active]:from-purple-600 data-[state=active]:to-purple-500 data-[state=active]:text-white rounded-lg transition-all"
              data-testid="tab-painel"
            >
              <BarChart3 className="w-4 h-4 mr-2" />
              Painel Global
            </TabsTrigger>
            <TabsTrigger 
              value="alunos"
              className="flex-1 min-w-[120px] data-[state=active]:bg-gradient-to-r data-[state=active]:from-purple-600 data-[state=active]:to-purple-500 data-[state=active]:text-white rounded-lg transition-all"
              data-testid="tab-alunos"
            >
              <Users className="w-4 h-4 mr-2" />
              Alunos
            </TabsTrigger>
            <TabsTrigger 
              value="risco"
              className="flex-1 min-w-[100px] data-[state=active]:bg-gradient-to-r data-[state=active]:from-purple-600 data-[state=active]:to-purple-500 data-[state=active]:text-white rounded-lg transition-all"
              data-testid="tab-risco"
            >
              <AlertTriangle className="w-4 h-4 mr-2" />
              Em Risco
            </TabsTrigger>
            <TabsTrigger 
              value="turma"
              className="flex-1 min-w-[120px] data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-600 data-[state=active]:to-cyan-500 data-[state=active]:text-white rounded-lg transition-all"
              data-testid="tab-turma"
            >
              <GraduationCap className="w-4 h-4 mr-2" />
              Por Turma
            </TabsTrigger>
            <TabsTrigger 
              value="executivo"
              className="flex-1 min-w-[120px] data-[state=active]:bg-gradient-to-r data-[state=active]:from-purple-600 data-[state=active]:to-pink-500 data-[state=active]:text-white rounded-lg transition-all"
              data-testid="tab-executivo"
            >
              <PieChart className="w-4 h-4 mr-2" />
              Executivo
            </TabsTrigger>
            <TabsTrigger 
              value="alertas"
              className="flex-1 min-w-[100px] data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-600 data-[state=active]:to-red-500 data-[state=active]:text-white rounded-lg transition-all"
              data-testid="tab-alertas"
            >
              <Bell className="w-4 h-4 mr-2" />
              Alertas
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="painel" className="mt-0">
            <PainelGlobal data={painelData} turmas={turmas} />
          </TabsContent>
          
          <TabsContent value="alunos" className="mt-0">
            <ListaAlunos turmas={turmas} />
          </TabsContent>
          
          <TabsContent value="risco" className="mt-0">
            <ListaAlunos turmas={turmas} filterRisco={true} />
          </TabsContent>
          
          <TabsContent value="turma" className="mt-0">
            <div className="mb-6">
              <Select value={selectedTurmaId} onValueChange={setSelectedTurmaId}>
                <SelectTrigger className="w-[300px] bg-slate-700/50 border-purple-500/20 text-purple-200">
                  <SelectValue placeholder="Selecione uma turma..." />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-purple-500/20">
                  {turmas.map((t) => (
                    <SelectItem key={t.id} value={t.id} className="text-purple-200 focus:bg-purple-500/20">
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DashboardTurma 
              turmaId={selectedTurmaId} 
              turmaNome={turmas.find(t => t.id === selectedTurmaId)?.name}
            />
          </TabsContent>
          
          <TabsContent value="executivo" className="mt-0">
            <DashboardExecutivo />
          </TabsContent>
          
          <TabsContent value="alertas" className="mt-0">
            <AlertasInteligentes />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
